import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { prefilledDocumentService } from '../services/prefilledDocumentService';
import { IPrefilledDocumentSummary } from '../types/prefilledDocument';
import { downloadTextAsPdf } from '../utils/textPdf';

const PrefilledDocuments: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<IPrefilledDocumentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const { data: documents = [], isLoading, error, refetch } = useQuery({
    queryKey: ['prefilled-documents'],
    queryFn: prefilledDocumentService.list,
  });

  const handleDownload = async (doc: IPrefilledDocumentSummary) => {
    try {
      setActionError('');
      const detail = await prefilledDocumentService.getById(doc.id);
      await downloadTextAsPdf(detail.title, detail.filledText);
    } catch {
      setActionError('Failed to download the document.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      setActionError('');
      await prefilledDocumentService.remove(deleteTarget.id);
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['prefilled-documents'] });
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to delete the document.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Pre-filled Documents
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          PDF forms filled from your clients' documents. Open a client document and choose "Pre-fill New File" to add one.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button size="small" onClick={() => refetch()}>Retry</Button>}>
          Failed to load pre-filled documents.
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: 'action.hover' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Filled From</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Unfilled Blanks</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No pre-filled documents yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{doc.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{doc.originalFilename}</Typography>
                  </TableCell>
                  <TableCell>{doc.sourceDocumentTitle}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={doc.unansweredCount === 0 ? 'All filled' : `${doc.unansweredCount} left blank`}
                      color={doc.unansweredCount === 0 ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="View">
                        <IconButton size="small" color="primary" aria-label="View" onClick={() => navigate(`/prefilled-documents/${doc.id}`)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="secondary" aria-label="Edit" onClick={() => navigate(`/prefilled-documents/${doc.id}?edit=true`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton size="small" aria-label="Download PDF" onClick={() => handleDownload(doc)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" aria-label="Delete" onClick={() => setDeleteTarget(doc)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete pre-filled document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{deleteTarget?.title}" will be permanently deleted. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmDelete} disabled={deleting} color="error" variant="contained">
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrefilledDocuments;
